import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const TOKEN_KEY = 'shopify_accounting_token';
const TENANT_KEY = 'shopify_accounting_tenant_id';

export interface User {
  id: number;
  email: string;
  name: string | null;
  role: 'user' | 'admin';
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

export interface Tenant {
  id: number;
  name: string;
  shopify_shop_name: string;
  is_active: boolean;
  cron_enabled: boolean;
  cron_time: string | null;
  cron_sync_start_date: string | null;
  gift_cards_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface SyncHistory {
  id: number;
  tenant_id: number;
  started_at: string;
  completed_at: string | null;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  start_date: string | null;
  end_date: string | null;
  orders_imported: number;
  transactions_imported: number;
  error_message: string | null;
  triggered_by: 'manual' | 'cron';
  created_at: string;
  updated_at: string;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token and tenant ID to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add tenant ID header if set
    const tenantId = localStorage.getItem(TENANT_KEY);
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear it
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TENANT_KEY);
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper to set current tenant
export const setCurrentTenant = (tenantId: number | null) => {
  if (tenantId) {
    localStorage.setItem(TENANT_KEY, String(tenantId));
  } else {
    localStorage.removeItem(TENANT_KEY);
  }
};

// Helper to get current tenant ID
export const getCurrentTenantId = (): number | null => {
  const id = localStorage.getItem(TENANT_KEY);
  return id ? parseInt(id, 10) : null;
};

// Auth API
export const authAPI = {
  register: (email: string, password: string, name?: string) =>
    api.post('/api/auth/register', { email, password, name }),

  login: (email: string, password: string) =>
    api.post('/api/auth/login', { email, password }),

  me: () => api.get('/api/auth/me'),

  getTenants: () => api.get('/api/auth/tenants'),

  switchTenant: (tenantId: number) =>
    api.post('/api/auth/switch-tenant', { tenant_id: tenantId }),

  updateProfile: (name: string | null) =>
    api.patch('/api/auth/profile', { name }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/api/auth/change-password', { currentPassword, newPassword }),
};

// Admin API
export const adminAPI = {
  // User management
  getUsers: () => api.get('/api/admin/users'),

  createUser: (data: { email: string; password: string; name?: string; role?: string; status?: string }) =>
    api.post('/api/admin/users', data),

  approveUser: (id: number) => api.patch(`/api/admin/users/${id}/approve`),

  rejectUser: (id: number) => api.patch(`/api/admin/users/${id}/reject`),

  updateUserRole: (id: number, role: 'user' | 'admin') =>
    api.patch(`/api/admin/users/${id}/role`, { role }),

  deleteUser: (id: number) => api.delete(`/api/admin/users/${id}`),

  updateUser: (id: number, data: { name?: string; email?: string }) =>
    api.patch(`/api/admin/users/${id}`, data),

  resetUserPassword: (id: number, newPassword: string) =>
    api.post(`/api/admin/users/${id}/reset-password`, { newPassword }),

  // Tenant management
  getTenants: (includeInactive?: boolean) =>
    api.get('/api/admin/tenants', { params: { include_inactive: includeInactive } }),

  getTenant: (id: number) => api.get(`/api/admin/tenants/${id}`),

  createTenant: (data: { 
    name: string; 
    shopify_shop_name: string; 
    shopify_access_token: string; 
    is_active?: boolean;
    cron_enabled?: boolean;
    cron_time?: string | null;
    cron_sync_start_date?: string | null;
  }) =>
    api.post('/api/admin/tenants', data),

  updateTenant: (id: number, data: { 
    name?: string; 
    shopify_shop_name?: string; 
    shopify_access_token?: string; 
    is_active?: boolean;
    cron_enabled?: boolean;
    cron_time?: string | null;
    cron_sync_start_date?: string | null;
  }) =>
    api.put(`/api/admin/tenants/${id}`, data),

  deleteTenant: (id: number) => api.delete(`/api/admin/tenants/${id}`),

  // Tenant-user assignment
  assignUserToTenant: (tenantId: number, userId: number) =>
    api.post(`/api/admin/tenants/${tenantId}/users`, { user_id: userId }),

  assignUsersToTenant: (tenantId: number, userIds: number[]) =>
    api.post(`/api/admin/tenants/${tenantId}/users`, { user_ids: userIds }),

  unassignUserFromTenant: (tenantId: number, userId: number) =>
    api.delete(`/api/admin/tenants/${tenantId}/users/${userId}`),

  getTenantUsers: (tenantId: number) =>
    api.get(`/api/admin/tenants/${tenantId}/users`),
};

// Shopify API
export const shopifyAPI = {
  syncOrders: (startDate?: string, endDate?: string) =>
    api.post('/api/shopify/sync', { start_date: startDate, end_date: endDate }),
  
  getSyncProgress: () =>
    api.get('/api/shopify/sync/progress'),
  
  getSyncHistory: (limit?: number) =>
    api.get('/api/shopify/sync/history', { params: { limit } }),
  
  getOrders: (params?: any) =>
    api.get('/api/shopify/orders', { params }),
  
  getOrder: (id: number) =>
    api.get(`/api/shopify/orders/${id}`),
  
  resyncOrder: (id: number) =>
    api.post(`/api/shopify/orders/${id}/resync`),
  
  exportOrders: (params?: any) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const tenantId = localStorage.getItem(TENANT_KEY);
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.financial_status) searchParams.append('financial_status', params.financial_status);
    if (params?.fulfillment_status) searchParams.append('fulfillment_status', params.fulfillment_status);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.payment_method_name) searchParams.append('payment_method_name', params.payment_method_name);
    if (token) searchParams.append('token', token);
    if (tenantId) searchParams.append('tenant_id', tenantId);
    const url = `${API_BASE_URL}/api/shopify/orders/export?${searchParams.toString()}`;
    window.open(url, '_blank');
  },
  
  getTransactions: (params?: any) =>
    api.get('/api/shopify/transactions', { params }),
  
  exportTransactions: (params?: any) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const tenantId = localStorage.getItem(TENANT_KEY);
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.transaction_type) searchParams.append('transaction_type', params.transaction_type);
    if (params?.payment_method_name) searchParams.append('payment_method_name', params.payment_method_name);
    if (token) searchParams.append('token', token);
    if (tenantId) searchParams.append('tenant_id', tenantId);
    const url = `${API_BASE_URL}/api/shopify/transactions/export?${searchParams.toString()}`;
    window.open(url, '_blank');
  },
  
  getPaymentMethods: () =>
    api.get('/api/shopify/payment-methods'),
  
  getMonthlyStats: (year: number) =>
    api.get(`/api/shopify/stats/monthly/${year}`),

  getUnpaidOrders: (asOfDate?: string, shipped?: 'shipped' | 'unshipped') =>
    api.get('/api/shopify/orders/unpaid', { params: { as_of_date: asOfDate, shipped } }),

  exportUnpaidOrders: (asOfDate?: string, statusFilter?: string, shipped?: 'shipped' | 'unshipped') => {
    const token = localStorage.getItem(TOKEN_KEY);
    const tenantId = localStorage.getItem(TENANT_KEY);
    const searchParams = new URLSearchParams();
    if (asOfDate) searchParams.append('as_of_date', asOfDate);
    if (statusFilter) searchParams.append('status', statusFilter);
    if (shipped) searchParams.append('shipped', shipped);
    if (token) searchParams.append('token', token);
    if (tenantId) searchParams.append('tenant_id', tenantId);
    const url = `${API_BASE_URL}/api/shopify/orders/unpaid/export?${searchParams.toString()}`;
    window.open(url, '_blank');
  },

  getGiftCards: (asOfDate?: string) =>
    api.get('/api/shopify/gift-cards', { params: { as_of_date: asOfDate } }),

  syncGiftCards: () =>
    api.post('/api/shopify/gift-cards/sync'),

  exportGiftCards: (asOfDate?: string, statusFilter?: string) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const tenantId = localStorage.getItem(TENANT_KEY);
    const searchParams = new URLSearchParams();
    if (asOfDate) searchParams.append('as_of_date', asOfDate);
    if (statusFilter) searchParams.append('status', statusFilter);
    if (token) searchParams.append('token', token);
    if (tenantId) searchParams.append('tenant_id', tenantId);
    const url = `${API_BASE_URL}/api/shopify/gift-cards/export?${searchParams.toString()}`;
    window.open(url, '_blank');
  },

  getUnshippedOrders: (asOfDate?: string) =>
    api.get('/api/shopify/orders/unshipped', { params: { as_of_date: asOfDate } }),

  exportUnshippedOrders: (asOfDate?: string, statusFilter?: string) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const tenantId = localStorage.getItem(TENANT_KEY);
    const searchParams = new URLSearchParams();
    if (asOfDate) searchParams.append('as_of_date', asOfDate);
    if (statusFilter) searchParams.append('status', statusFilter);
    if (token) searchParams.append('token', token);
    if (tenantId) searchParams.append('tenant_id', tenantId);
    const url = `${API_BASE_URL}/api/shopify/orders/unshipped/export?${searchParams.toString()}`;
    window.open(url, '_blank');
  },
};

export default api;
